import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  CalendarDays, Plus, Loader2, Trash2, ArrowLeft, ShieldCheck,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * Small settings screen listing every public holiday so Admin / Director /
 * Design Head can add or remove entries without touching the API. Read
 * access is open (so anyone can see the calendar); write actions are gated
 * server-side and hidden here when the caller isn't editable.
 */
export default function HolidaysSettingsPage() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [editable, setEditable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/design/holidays");
      setRows(data.holidays || []);
      setEditable(Boolean(data.editable));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addHoliday = async () => {
    if (!date) { toast.error("Pick a date"); return; }
    if (name.trim().length < 2) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      await api.post("/design/holidays", { date, name: name.trim() });
      toast.success("Holiday added");
      setDate(""); setName("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to add holiday");
    } finally { setBusy(false); }
  };

  const removeHoliday = async (hid, hname) => {
    if (!window.confirm(`Delete "${hname}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/design/holidays/${hid}`);
      toast.success("Holiday deleted");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  // Group by month for readability
  const byMonth = rows.reduce((acc, h) => {
    const dt = new Date(`${h.date}T00:00:00`);
    const key = dt.toLocaleString("en-IN", { month: "long", year: "numeric" });
    (acc[key] = acc[key] || []).push(h);
    return acc;
  }, {});

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-6 md:px-10 py-6 bg-[#050505]/80 sticky top-0 z-30">
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={() => nav(-1)}
                  className="iz-btn-secondary text-xs flex items-center gap-1.5"
                  data-testid="holidays-back">
            <ArrowLeft className="w-3.5 h-3.5"/> Back
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">
              Settings · Calendar
            </p>
            <h1 className="font-display text-2xl md:text-3xl text-white mt-1">Public Holidays</h1>
            <p className="text-[11px] text-gray-500 mt-1 italic">
              These days are automatically excluded from every designer's
              Next Target Date calculation.
            </p>
          </div>
          {editable && (
            <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 border border-emerald-500/40 rounded px-1.5 py-0.5 flex items-center gap-1"
                  data-testid="holidays-edit-badge">
              <ShieldCheck className="w-3 h-3"/> Editable
            </span>
          )}
        </div>
      </div>

      <div className="px-6 md:px-10 py-6 space-y-5">
        {editable && (
          <div className="iz-card p-4" data-testid="add-holiday-form">
            <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 flex items-center gap-1 mb-3">
              <Plus className="w-3.5 h-3.5"/> Add Holiday
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input type="date" value={date}
                     onChange={(e) => setDate(e.target.value)}
                     className="iz-input text-xs"
                     data-testid="holiday-date"/>
              <input value={name} onChange={(e) => setName(e.target.value)}
                     placeholder="e.g. Regional Bandh"
                     className="iz-input text-xs sm:col-span-2"
                     data-testid="holiday-name"/>
            </div>
            <button onClick={addHoliday} disabled={busy}
                    className="iz-btn-primary text-xs flex items-center gap-1.5 mt-3"
                    data-testid="holiday-save">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Plus className="w-3.5 h-3.5"/>}
              Save Holiday
            </button>
          </div>
        )}

        <div className="iz-card p-4" data-testid="holidays-list">
          <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-3 flex items-center gap-1">
            <CalendarDays className="w-3.5 h-3.5"/> All Holidays ({rows.length})
          </p>
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin"/> Loading…
            </div>
          ) : rows.length === 0 ? (
            <p className="text-xs text-gray-500 italic">No holidays configured.</p>
          ) : (
            <div className="space-y-4">
              {Object.entries(byMonth).map(([month, entries]) => (
                <div key={month} data-testid={`holidays-month-${month}`}>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37] mb-2">
                    {month}
                  </p>
                  <ul className="space-y-1">
                    {entries.map((h) => (
                      <li key={h.id}
                          className="flex items-center gap-3 border border-[#161616] rounded px-3 py-2 text-xs"
                          data-testid={`holiday-row-${h.id}`}>
                        <span className="text-white font-mono min-w-[100px]">{h.date}</span>
                        <span className="text-gray-300 flex-1 truncate">{h.name}</span>
                        {h.seeded && (
                          <span className="text-[9px] font-mono uppercase tracking-widest text-gray-500 border border-[#262626] rounded px-1 py-0.5">
                            seeded
                          </span>
                        )}
                        {editable && (
                          <button onClick={() => removeHoliday(h.id, h.name)}
                                  disabled={busy}
                                  className="text-gray-500 hover:text-red-400"
                                  data-testid={`holiday-delete-${h.id}`}>
                            <Trash2 className="w-3.5 h-3.5"/>
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
